import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMore from '@mui/icons-material/ExpandMore';
import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, Paper, List, ListItem, ListItemText, Tooltip, ListSubheader, Tabs, Tab } from '@mui/material';
import { CheckCircle, AssignmentTurnedIn } from '@mui/icons-material';
import RelationalCard from './RelationalCard';
import { Add, Edit } from '@mui/icons-material';
import { Button, IconButton } from '@mui/material';

import { Goal } from '../../../Models/CarePlans/Goal.model';

import { CarePlanEnum } from '../../../Enum/CarePlanEnum';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { RootState } from '../../../reducers';
import {
    addBarrier,
    addGoal,
    addMetric,
    addNextStep,
    addPlan,
    addSchedulePlan,
    addStrength,
    editBarriers,
    editGoals,
    editMetrics,
    editNextSteps,
    editPlans,
    editSchedulePlan,
    editStrengths,
    getCarePlanByMemberId,
    getRoles,
    handleEdit,
    handleProgressSave,
    setDraggedItem,
    setEditDialogOpen,
    setEditDueDate,
    setEditItem,
    setEditLabel,
    setEditStatus,
    setEditTimeIntervalInDays,
    setEditTimeIntervalType,
    setEditType,
    setGoalStatusTab,
    setMemberSignatureRequest,
    setProgressModal,
    setProviderPreview,
    setProviderSignatureRequest,
} from '../../../store/carePlanUpdate.slice';
import { useParams } from 'react-router-dom';
import ReviewSignModal from './ReviewSignModal';
import { TimeIntervalTypeEnum } from '../../../Enum/TimeIntervalTypeEnum';

// Refactored imports - using constants and utilities for cleaner code
import { GOAL_STATUSES } from './constants';

import { isValid, isBefore } from 'date-fns';
import { toDate } from '../../../utils/dateHelpers';
import { Progress } from '../../../Models/CarePlans/Progress.model';
import { EditDialog, ProgressModal } from './Modals';
import { Strength } from '../../../Models/CarePlans/Strength.model';
import { Metric } from '../../../Models/CarePlans/Metric.model';
import { Plan } from '../../../Models/CarePlans/Plan.model';
import { NextStep } from '../../../Models/CarePlans/NextStep.model';
import { Barrier } from '../../../Models/CarePlans/Barrier.model';
import { ItemType } from './types';
import CarePlanTopSection from './CarePlanTopSection';
import { getNewProgressModalState } from './utils';
import GoalsColumn from './GoalsColumn';
import { SchedulePlan } from '../../../Models/CarePlans/SchedulePlan.model';
import SignatureModal from './SignatureModal';
import { setShowMessage } from '../../../store/shared.slice';
import { getActiveProviders } from '../../../store/provider.slice';
import { CreatedBy } from '../../../Models/Shared/CreatedBy.model';
import Authorization from '../../../utils/Authorization';

interface CarePlanProps {
    isAssessment?: boolean;
}

const CarePlan: React.FC<CarePlanProps> = ({ isAssessment = false }) => {
    const authData = new Authorization();
    const params = useParams<{ memberId: string }>();
    const memberId = params.memberId;
    const {
        editDialogOpen,
        editDueDate,
        editItem,
        editLabel,
        editStatus,
        editType,
        editTimeIntervalInDays,
        editTimeIntervalType,
        goalStatusTab,
        integratedCarePlan,
        progressModal,
        providerPreview,
    } = useSelector((state: RootState) => state.carePlanUpdate);
    const dispatch = useAppDispatch();
    const [reviewSignModalOpen, setReviewSignModalOpen] = useState(false);

    const handleDragStart = (item: any, type: string) => (e: React.DragEvent) => {
        dispatch(setDraggedItem({ ...item, type }));
        e.dataTransfer.effectAllowed = 'move';
    };
    const handleDragEnd = () => {
        dispatch(setDraggedItem(null));
    };
    const mostRecentMemberReview = integratedCarePlan.ReviewHistoryMember?.[0] || null;

    // Simplified state setters using extracted utility
    const strengths = integratedCarePlan.Strengths || [];
    const plans = integratedCarePlan.Plans || [];
    const barriers = integratedCarePlan.Barriers || [];
    const goals = integratedCarePlan.Goals || [];
    const nextSteps = integratedCarePlan.NextSteps || [];
    const schedulePlans = integratedCarePlan.SchedulePlans || [];

    const reviewSignGoals = integratedCarePlan.Goals.filter(
        (g) =>
            g.Status === CarePlanEnum.InProgress ||
            (g.Status === CarePlanEnum.Achieved &&
                (() => {
                    const reviewDate = toDate(mostRecentMemberReview?.LastReviewedOn);
                    const updatedDate = toDate(g.UpdatedAt);
                    return reviewDate && updatedDate ? isBefore(reviewDate, updatedDate) : false;
                })())
    );

    const cancelReviewAndSignModal = () => {
        setReviewSignModalOpen(false);
    };

    useEffect(() => {
        dispatch(getRoles());
        dispatch(getActiveProviders(false));
    }, [dispatch]);

    useEffect(() => {
        dispatch(getCarePlanByMemberId(memberId));
    }, [dispatch, memberId]);

    useEffect(() => {
        if (integratedCarePlan && integratedCarePlan.Id) {
            if (integratedCarePlan.ReviewHistoryMember && integratedCarePlan.ReviewHistoryMember.length > 0) {
                const mostRecentMemberReview = integratedCarePlan.ReviewHistoryMember[0];
                dispatch(setMemberSignatureRequest(mostRecentMemberReview));
            }
            if (integratedCarePlan.ReviewHistory && integratedCarePlan.ReviewHistory.length > 0) {
                const mostRecentProviderReview = integratedCarePlan.ReviewHistory[0];
                dispatch(setProviderSignatureRequest(mostRecentProviderReview));
            }
        }
    }, [dispatch, integratedCarePlan]);

    // Only one right-side accordion section open at a time
    const [rightAccordionExpanded, setRightAccordionExpanded] = useState<
        'strengths' | 'barriers' | 'nextSteps' | 'plans' | 'schedulePlans' | 'metrics' | false
    >(false);

    // CRUD handlers (edit, add, delete)
    const handleAdd = (type: ItemType) => {
        dispatch(setEditType(type));
        dispatch(setEditLabel(''));
        dispatch(setEditDueDate(null));
        dispatch(setEditDialogOpen(true));
        dispatch(setProviderPreview(null));
        dispatch(setEditTimeIntervalInDays(0));
    };

    const handleEditSave = () => {
        if (!editType || !editItem) {
            dispatch(setShowMessage(true, 'No item to save', 'error'));
            return;
        }
        const isNewItem = editItem.Id === null || editItem.Id === undefined;

        if (isNewItem) {
            handleAddItems();
        } else {
            handleEditItem();
        }
    };

    const handleEditItem = () => {
        let updatedBy = new CreatedBy();
        updatedBy.Id = authData.UserId;
        updatedBy.Name = authData.UserName;
        switch (editType) {
            case 'goal':
                const updatedGoal = {
                    ...editItem,
                    Name: editLabel,
                    Status: editStatus,
                    DueDate: editDueDate ? (editDueDate instanceof Date ? editDueDate.toISOString() : new Date(editDueDate).toISOString()) : null,
                };
                dispatch(editGoals(memberId, [updatedGoal], updatedBy));
                break;
            case 'strength':
                const updatedStrength = { ...editItem, Name: editLabel, Id: editItem.Id };
                dispatch(editStrengths(memberId, [updatedStrength]));
                break;
            case 'barrier':
                const updatedBarrier = { ...editItem, Name: editLabel, Id: editItem.Id };
                dispatch(editBarriers(memberId, [updatedBarrier]));
                break;
            case 'plan':
                const updatedPlan = { ...editItem, Name: editLabel, Id: editItem.Id };
                dispatch(editPlans(memberId, [updatedPlan]));
                break;
            case 'nextStep':
                const updatedNextStep = { ...editItem, Name: editLabel, Id: editItem.Id };
                dispatch(editNextSteps(memberId, [updatedNextStep]));
                break;
            case 'metric':
                const updatedMetric = { ...editItem, Name: editLabel, Id: editItem.Id };
                dispatch(editMetrics(memberId, [updatedMetric]));
                break;
            case 'schedulePlan':
                const updatedSchedulePlan = {
                    ...editItem,
                    Provider: providerPreview,
                    TimeIntervalType: editTimeIntervalType,
                    TimeIntervalIndays: editTimeIntervalInDays,
                    Id: editItem.Id,
                } as SchedulePlan;
                dispatch(editSchedulePlan(memberId, updatedSchedulePlan));
                break;
            default:
                break;
        }
    };

    const handleAddItems = () => {
        switch (editType) {
            case 'goal':
                dispatch(
                    addGoal(
                        memberId,
                        editLabel,
                        editDueDate ? (editDueDate instanceof Date ? editDueDate.toISOString() : new Date(editDueDate).toISOString()) : null
                    )
                );
                break;
            case 'strength':
                dispatch(addStrength(memberId, editLabel));
                break;
            case 'barrier':
                dispatch(addBarrier(memberId, editLabel));
                break;
            case 'plan':
                dispatch(addPlan(memberId, editLabel));
                break;
            case 'nextStep':
                dispatch(addNextStep(memberId, editLabel));
                break;
            case 'metric':
                dispatch(addMetric(memberId, editLabel));
                break;
            case 'schedulePlan':
                dispatch(
                    addSchedulePlan(memberId, {
                        Provider: providerPreview,
                        TimeIntervalType: editTimeIntervalType,
                        TimeIntervalIndays: editTimeIntervalInDays,
                    } as SchedulePlan)
                );

                break;
            default:
                break;
        }
    };

    const handleClickSignAndReview = () => {
        if (reviewSignGoals.length === 0) {
            dispatch(setShowMessage(true, 'No goals to review and sign', 'error'));
            return;
        }
        setReviewSignModalOpen(true);
    };

    return (
        <Paper elevation={3} sx={{ p: 0, pb: 5, bgcolor: '#f5f7fa', borderRadius: 3 }}>
            <CarePlanTopSection isAssessment={isAssessment} />
            {/* Drag-and-drop Relationship Builder (from CarePlanUpdateTwo) */}
            <Box mb={2}>
                <Typography variant="h5" fontWeight={700} align="center" sx={{ letterSpacing: 1 }}>
                    Care Plan Goal Builder
                </Typography>
                <Typography variant="body1" color="text.secondary" textAlign="center">
                    Drag and drop strengths, barriers, plans, next steps, metrics, and schedule plans onto goals.
                </Typography>
            </Box>

            <Box sx={{ mb: 2, p: 0, pb: 2, border: '1px solid #e3e6f0', borderRadius: 2, bgcolor: '#fff', minHeight: '600px', position: 'relative' }}>
                <Grid container>
                    {/* Left: Accordion for Strengths, Barriers, Methods, Schedule Plans */}
                    <Grid
                        size={{ xs: 12, sm: 5, md: 3 }}
                        sx={{ position: 'sticky', top: '22vh', overflowY: 'auto', maxHeight: '70vh', mt: 14, pb: 1, pr: 0.5 }}
                    >
                        <Accordion
                            expanded={rightAccordionExpanded === 'strengths'}
                            onChange={(_, expanded) => setRightAccordionExpanded(expanded ? 'strengths' : false)}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography fontWeight={600} color="success.main">
                                    Strengths ({strengths.length})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={1}>
                                    <Grid size={12}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            color="success"
                                            fullWidth
                                            startIcon={<Add />}
                                            onClick={() => {
                                                handleAdd('strength');
                                                dispatch(setEditItem(new Strength()));
                                            }}
                                            sx={{ mb: 2 }}
                                        >
                                            Add Strength
                                        </Button>
                                    </Grid>
                                    {strengths.map((s) => (
                                        <Grid size={12} key={s.Id}>
                                            <RelationalCard
                                                icon={<CheckCircle color="success" />}
                                                label={s.Name}
                                                updatedAt={s.UpdatedAt ? s.UpdatedAt : s.CreatedAt}
                                                type="strength"
                                                children={
                                                    <List dense sx={{ mt: 1, mb: 1 }}>
                                                        <ListSubheader
                                                            sx={{
                                                                fontWeight: 'bold',
                                                                pl: 0,
                                                                justifyContent: 'space-between',
                                                                display: 'flex',
                                                                lineHeight: 'normal',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            Progress
                                                            <Tooltip title="Add Progress Entry" arrow>
                                                                <IconButton
                                                                    size="small"
                                                                    color="primary"
                                                                    onClick={() =>
                                                                        dispatch(
                                                                            setProgressModal({
                                                                                open: true,
                                                                                goalId: s.Id,
                                                                                progressId: null,
                                                                                value: '',
                                                                                type: 'strength',
                                                                            })
                                                                        )
                                                                    }
                                                                >
                                                                    <Add fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </ListSubheader>
                                                        {s.Progress && s.Progress.length > 0 ? (
                                                            s.Progress.map((p: Progress) => (
                                                                <ListItem key={p.Id}>
                                                                    <ListItemText primary={p.Name} secondary={`${new Date(p.CreatedAt).toLocaleString()}`} />
                                                                </ListItem>
                                                            ))
                                                        ) : (
                                                            <ListItem>
                                                                <ListItemText
                                                                    sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                                                                    primary="No progress recorded"
                                                                />
                                                            </ListItem>
                                                        )}
                                                    </List>
                                                }
                                                length={s.Progress ? s.Progress.length : 0}
                                                isDraggable
                                                onDragStart={handleDragStart(s, 'strength')}
                                                onDragEnd={handleDragEnd}
                                                actions={
                                                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
                                                        <Tooltip title="Edit Strength" arrow>
                                                            <IconButton size="small" onClick={() => dispatch(handleEdit(s, 'strength'))}>
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                }
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                        <Accordion
                            expanded={rightAccordionExpanded === 'barriers'}
                            onChange={(_, expanded) => setRightAccordionExpanded(expanded ? 'barriers' : false)}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography fontWeight={600} color="error.main">
                                    Barriers ({barriers.length})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={2}>
                                    <Grid size={12}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            color="error"
                                            fullWidth
                                            startIcon={<Add />}
                                            onClick={() => {
                                                handleAdd('barrier');
                                                dispatch(setEditItem(new Barrier()));
                                            }}
                                            sx={{ mb: 2 }}
                                        >
                                            Add Barrier
                                        </Button>
                                    </Grid>
                                    {barriers.map((b) => (
                                        <Grid size={12} key={b.Id}>
                                            <RelationalCard
                                                icon={<AssignmentTurnedIn color="error" />}
                                                label={b.Name}
                                                updatedAt={b.UpdatedAt ? b.UpdatedAt : b.CreatedAt}
                                                type="barrier"
                                                children={
                                                    <List dense sx={{ mt: 1, mb: 1 }}>
                                                        <ListSubheader
                                                            sx={{
                                                                fontWeight: 'bold',
                                                                pl: 0,
                                                                justifyContent: 'space-between',
                                                                display: 'flex',
                                                                lineHeight: 'normal',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            Progress
                                                            <Tooltip title="Add Progress Entry" arrow>
                                                                <IconButton
                                                                    size="small"
                                                                    color="primary"
                                                                    onClick={() =>
                                                                        dispatch(
                                                                            setProgressModal({
                                                                                open: true,
                                                                                goalId: b.Id,
                                                                                progressId: null,
                                                                                value: '',
                                                                                type: 'barrier',
                                                                            })
                                                                        )
                                                                    }
                                                                >
                                                                    <Add fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </ListSubheader>
                                                        {b.Progress && b.Progress.length > 0 ? (
                                                            b.Progress.map((p: Progress) => (
                                                                <ListItem key={p.Id}>
                                                                    <ListItemText primary={p.Name} secondary={`${new Date(p.CreatedAt).toLocaleString()}`} />
                                                                </ListItem>
                                                            ))
                                                        ) : (
                                                            <ListItem>
                                                                <ListItemText
                                                                    sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                                                                    primary="No progress recorded"
                                                                />
                                                            </ListItem>
                                                        )}
                                                    </List>
                                                }
                                                length={b.Progress ? b.Progress.length : 0}
                                                isDraggable
                                                onDragStart={handleDragStart(b, 'barrier')}
                                                onDragEnd={handleDragEnd}
                                                actions={
                                                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
                                                        <Tooltip title="Edit Barrier" arrow>
                                                            <IconButton size="small" onClick={() => dispatch(handleEdit(b, 'barrier'))}>
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                }
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            expanded={rightAccordionExpanded === 'plans'}
                            onChange={(_, expanded) => setRightAccordionExpanded(expanded ? 'plans' : false)}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography fontWeight={600} color="info.dark">
                                    Plans ({plans.length})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={2}>
                                    <Grid size={12}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            color="info"
                                            fullWidth
                                            startIcon={<Add />}
                                            onClick={() => {
                                                handleAdd('plan');
                                                dispatch(setEditItem(new Plan()));
                                            }}
                                            sx={{ mb: 2 }}
                                        >
                                            Add Plan
                                        </Button>
                                    </Grid>
                                    {plans && plans.length > 0 ? (
                                        plans.map((p) => (
                                            <Grid size={12} key={p.Id}>
                                                <RelationalCard
                                                    icon={<AssignmentTurnedIn color="info" />}
                                                    label={p.Name}
                                                    type="plan"
                                                    updatedAt={p.UpdatedAt ? p.UpdatedAt : p.CreatedAt}
                                                    children={
                                                        <List dense sx={{ mt: 1, mb: 1 }}>
                                                            <ListSubheader
                                                                sx={{
                                                                    fontWeight: 'bold',
                                                                    pl: 0,
                                                                    justifyContent: 'space-between',
                                                                    display: 'flex',
                                                                    lineHeight: 'normal',
                                                                    alignItems: 'center',
                                                                }}
                                                            >
                                                                Progress
                                                                <Tooltip title="Add Progress Entry" arrow>
                                                                    <IconButton
                                                                        size="small"
                                                                        color="primary"
                                                                        onClick={() =>
                                                                            dispatch(
                                                                                setProgressModal({
                                                                                    open: true,
                                                                                    goalId: p.Id,
                                                                                    progressId: null,
                                                                                    value: '',
                                                                                    type: 'plan',
                                                                                })
                                                                            )
                                                                        }
                                                                    >
                                                                        <Add fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </ListSubheader>
                                                            {p.Progress && p.Progress.length > 0 ? (
                                                                p.Progress.map((p: Progress) => (
                                                                    <ListItem key={p.Id}>
                                                                        <ListItemText
                                                                            primary={p.Name}
                                                                            secondary={`${new Date(p.CreatedAt).toLocaleString()}`}
                                                                        />
                                                                    </ListItem>
                                                                ))
                                                            ) : (
                                                                <ListItem>
                                                                    <ListItemText
                                                                        sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                                                                        primary="No progress recorded"
                                                                    />
                                                                </ListItem>
                                                            )}
                                                        </List>
                                                    }
                                                    length={p.Progress ? p.Progress.length : 0}
                                                    isDraggable
                                                    onDragStart={handleDragStart(p, 'plan')}
                                                    onDragEnd={handleDragEnd}
                                                    actions={
                                                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
                                                            <Tooltip title="Edit Plan" arrow>
                                                                <IconButton size="small" onClick={() => dispatch(handleEdit(p, 'plan'))}>
                                                                    <Edit fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    }
                                                />
                                            </Grid>
                                        ))
                                    ) : (
                                        <Grid size={12}>
                                            <Typography fontStyle={'italic'} color="text.secondary" variant="body2" sx={{ mt: 1 }}>
                                                No plans recorded
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                        <Accordion
                            expanded={rightAccordionExpanded === 'nextSteps'}
                            onChange={(_, expanded) => setRightAccordionExpanded(expanded ? 'nextSteps' : false)}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography fontWeight={600} color="secondary.main">
                                    Next Steps ({nextSteps.length})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={2}>
                                    <Grid size={12}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            color="secondary"
                                            fullWidth
                                            startIcon={<Add />}
                                            onClick={() => {
                                                handleAdd('nextStep');
                                                dispatch(setEditItem(new NextStep()));
                                            }}
                                            sx={{ mb: 2 }}
                                        >
                                            Add Next Step
                                        </Button>
                                    </Grid>
                                    {nextSteps.map((m) => (
                                        <Grid size={12} key={m.Id}>
                                            <RelationalCard
                                                icon={<AssignmentTurnedIn color="secondary" />}
                                                label={m.Name}
                                                updatedAt={m.UpdatedAt ? m.UpdatedAt : m.CreatedAt}
                                                type="nextStep"
                                                children={
                                                    <List dense sx={{ mt: 1, mb: 1 }}>
                                                        <ListSubheader
                                                            sx={{
                                                                fontWeight: 'bold',
                                                                pl: 0,
                                                                justifyContent: 'space-between',
                                                                display: 'flex',
                                                                lineHeight: 'normal',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            Progress
                                                            <Tooltip title="Add Progress Entry" arrow>
                                                                <IconButton
                                                                    size="small"
                                                                    color="primary"
                                                                    onClick={() =>
                                                                        dispatch(
                                                                            setProgressModal({
                                                                                open: true,
                                                                                goalId: m.Id,
                                                                                progressId: null,
                                                                                value: '',
                                                                                type: 'nextStep',
                                                                            })
                                                                        )
                                                                    }
                                                                >
                                                                    <Add fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </ListSubheader>
                                                        {m.Progress && m.Progress.length > 0 ? (
                                                            m.Progress.map((p: Progress) => (
                                                                <ListItem key={p.Id}>
                                                                    <ListItemText primary={p.Name} secondary={`${new Date(p.CreatedAt).toLocaleString()}`} />
                                                                </ListItem>
                                                            ))
                                                        ) : (
                                                            <ListItem>
                                                                <ListItemText
                                                                    sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                                                                    primary="No progress recorded"
                                                                />
                                                            </ListItem>
                                                        )}
                                                    </List>
                                                }
                                                length={m.Progress ? m.Progress.length : 0}
                                                isDraggable
                                                onDragStart={handleDragStart(m, 'nextStep')}
                                                onDragEnd={handleDragEnd}
                                                actions={
                                                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
                                                        <Tooltip title="Edit Next Step" arrow>
                                                            <IconButton size="small" onClick={() => dispatch(handleEdit(m, 'nextStep'))}>
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                }
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                        <Accordion
                            expanded={rightAccordionExpanded === 'metrics'}
                            onChange={(_, expanded) => setRightAccordionExpanded(expanded ? 'metrics' : false)}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography fontWeight={600} color="info.light">
                                    Metrics ({integratedCarePlan.Metrics?.length || 0})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={2}>
                                    <Grid size={12}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            color="info"
                                            fullWidth
                                            startIcon={<Add />}
                                            onClick={() => {
                                                handleAdd('metric');
                                                dispatch(setEditItem(new Metric()));
                                            }}
                                            sx={{ mb: 2 }}
                                        >
                                            Add Metric
                                        </Button>
                                    </Grid>
                                    {integratedCarePlan.Metrics && integratedCarePlan.Metrics.length > 0 ? (
                                        integratedCarePlan.Metrics.map((m) => (
                                            <Grid size={12} key={m.Id}>
                                                <RelationalCard
                                                    icon={<AssignmentTurnedIn color="info" />}
                                                    label={m.Name}
                                                    updatedAt={m.UpdatedAt ? m.UpdatedAt : m.CreatedAt}
                                                    type="metric"
                                                    children={
                                                        <List dense sx={{ mt: 1, mb: 1 }}>
                                                            <ListSubheader
                                                                sx={{
                                                                    fontWeight: 'bold',
                                                                    pl: 0,
                                                                    justifyContent: 'space-between',
                                                                    display: 'flex',
                                                                    lineHeight: 'normal',
                                                                    alignItems: 'center',
                                                                }}
                                                            >
                                                                Progress
                                                                <Tooltip title="Add Progress Entry" arrow>
                                                                    <IconButton
                                                                        size="small"
                                                                        color="primary"
                                                                        onClick={() =>
                                                                            dispatch(
                                                                                setProgressModal({
                                                                                    open: true,
                                                                                    goalId: m.Id,
                                                                                    progressId: null,
                                                                                    value: '',
                                                                                    type: 'metric',
                                                                                })
                                                                            )
                                                                        }
                                                                    >
                                                                        <Add fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </ListSubheader>
                                                            {m.Progress && m.Progress.length > 0 ? (
                                                                m.Progress.map((p: Progress) => (
                                                                    <ListItem key={p.Id}>
                                                                        <ListItemText
                                                                            primary={p.Name}
                                                                            secondary={`${new Date(p.CreatedAt).toLocaleString()}`}
                                                                        />
                                                                    </ListItem>
                                                                ))
                                                            ) : (
                                                                <ListItem>
                                                                    <ListItemText
                                                                        sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                                                                        primary="No progress recorded"
                                                                    />
                                                                </ListItem>
                                                            )}
                                                        </List>
                                                    }
                                                    length={m.Progress ? m.Progress.length : 0}
                                                    isDraggable
                                                    onDragStart={handleDragStart(m, 'metric')}
                                                    onDragEnd={handleDragEnd}
                                                    actions={
                                                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
                                                            <Tooltip title="Edit Metric" arrow>
                                                                <IconButton size="small" onClick={() => dispatch(handleEdit(m, 'metric'))}>
                                                                    <Edit fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    }
                                                />
                                            </Grid>
                                        ))
                                    ) : (
                                        <Grid size={12}>
                                            <Typography fontStyle={'italic'} color="text.secondary" variant="body2" sx={{ mt: 1 }}>
                                                No metrics recorded
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                        <Accordion
                            expanded={rightAccordionExpanded === 'schedulePlans'}
                            onChange={(_, expanded) => setRightAccordionExpanded(expanded ? 'schedulePlans' : false)}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography fontWeight={600} color="warning.main">
                                    Schedule Plans ({integratedCarePlan.SchedulePlans?.length || 0})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={2}>
                                    <Grid size={12}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            color="warning"
                                            fullWidth
                                            startIcon={<Add />}
                                            onClick={() => {
                                                handleAdd('schedulePlan');
                                                dispatch(setEditItem(new SchedulePlan()));
                                            }}
                                            sx={{ mb: 2 }}
                                        >
                                            Add Schedule Plan
                                        </Button>
                                    </Grid>
                                    {schedulePlans && schedulePlans.length > 0 ? (
                                        schedulePlans.map((sp) => (
                                            <Grid size={12} key={sp.Id}>
                                                <RelationalCard
                                                    icon={<AssignmentTurnedIn color="warning" />}
                                                    label={sp.Provider?.Role || '—'}
                                                    children={
                                                        <>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Appointment with <strong>{sp.Provider?.Role || '—'}</strong> occurs every{' '}
                                                                <strong>
                                                                    {sp.TimeIntervalIndays} {TimeIntervalTypeEnum[sp.TimeIntervalType]}
                                                                </strong>
                                                            </Typography>
                                                        </>
                                                    }
                                                    length={0}
                                                    type="schedulePlan"
                                                    isDraggable
                                                    onDragStart={handleDragStart(sp, 'schedulePlan')}
                                                    onDragEnd={handleDragEnd}
                                                    actions={
                                                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
                                                            <Tooltip title="Edit Schedule Plan" arrow>
                                                                <IconButton size="small" onClick={() => dispatch(handleEdit(sp, 'schedulePlan'))}>
                                                                    <Edit fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    }
                                                />
                                            </Grid>
                                        ))
                                    ) : (
                                        <Grid size={12}>
                                            <Typography fontStyle={'italic'} color="text.secondary" variant="body2" sx={{ mt: 1 }}>
                                                No schedule plans recorded
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                    {/* Right: Goals */}
                    <Grid size={{ xs: 12, sm: 7, md: 9 }} sx={{ pl: 1 }}>
                        <Box width={'100%'} display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="h5" color="primary.main" fontWeight={600} textAlign={'center'} sx={{ flexGrow: 1 }}>
                                Goals
                            </Typography>
                            <Tooltip title="Add New Goal" arrow>
                                <Button
                                    variant="contained"
                                    // size="small"
                                    color="primary"
                                    startIcon={<Add />}
                                    onClick={() => {
                                        handleAdd('goal');
                                        dispatch(setEditItem(new Goal()));
                                    }}
                                    sx={{ justifySelf: 'flex-end', textTransform: 'none', margin: 2 }}
                                >
                                    Add Goal
                                </Button>
                            </Tooltip>
                        </Box>
                        {/* Tabs for Goal Status */}
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                            <Tabs
                                value={goalStatusTab}
                                onChange={(_, newValue) => dispatch(setGoalStatusTab(newValue))}
                                variant="scrollable"
                                scrollButtons="auto"
                                aria-label="Goal Status Tabs"
                            >
                                {GOAL_STATUSES.filter((status) => status.value !== CarePlanEnum.NotMet && status.value !== CarePlanEnum.Deleted).map(
                                    (status) => {
                                        const count = goals.filter((g) => g.Status === status.value).length;
                                        return <Tab key={status.value} label={`${status.label} (${count})`} value={status.value} />;
                                    }
                                )}
                            </Tabs>
                        </Box>
                        {/* Vertically scrollable Goals column */}
                        <GoalsColumn isAssessment={isAssessment} />
                    </Grid>
                </Grid>
            </Box>
            <EditDialog
                editDialog={{
                    open: editDialogOpen,
                    type: editType,
                    label: editLabel,
                    status: editStatus,
                    item: editItem,
                    dueDate: editDueDate,
                    provider: providerPreview,
                    timeIntervalType: editTimeIntervalType,
                    timeIntervalIndays: editTimeIntervalInDays,
                }}
                onClose={() => dispatch(setEditDialogOpen(false))}
                onSave={handleEditSave}
                onLabelChange={(value) => dispatch(setEditLabel(value))}
                onStatusChange={(value) => dispatch(setEditStatus(value as CarePlanEnum))}
                onDueDateChange={(value) => dispatch(setEditDueDate(value))}
                onProviderChange={(value) => dispatch(setProviderPreview(value))}
                onTimeIntervalChange={(value) => dispatch(setEditTimeIntervalType(value))}
                onTimeIntervalInDaysChange={(value) => dispatch(setEditTimeIntervalInDays(value))}
            />

            <SignatureModal />
            <ProgressModal
                progressModal={progressModal}
                onClose={() => dispatch(setProgressModal(getNewProgressModalState()))}
                onValueChange={(value) => dispatch(setProgressModal({ ...progressModal, value }))}
                onSave={() => dispatch(handleProgressSave(memberId))}
            />
            {/* Floating Review & Sign Button -  */}
            <Box
                sx={{
                    position: 'fixed',
                    bottom: isAssessment ? 64 : 16,
                    right: 16,
                    zIndex: 1300,
                    boxShadow: 4,
                }}
            >
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    sx={{
                        minWidth: 120,
                        fontWeight: 700,
                    }}
                    onClick={handleClickSignAndReview}
                >
                    Review & Sign
                </Button>
            </Box>
            <ReviewSignModal open={reviewSignModalOpen} onClose={cancelReviewAndSignModal} reviewSignGoals={reviewSignGoals} />
        </Paper>
    );
};

export default CarePlan;
